import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "../../../../../theme/theme";

const IFRAME_MIN_HEIGHT = 700;

const GHLCalendarModal = ({ open, onClose, calendarUrl, title, onBookingDetected }) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const iframeRef = useRef(null);

  // Reset loading state when modal opens with a new URL
  useEffect(() => {
    if (open) {
      setIframeLoading(true);
    }
  }, [open, calendarUrl]);

  // Listen for postMessage from the GHL iframe to detect booking completion
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event) => {
      // GHL sends various message types; booking completion is signaled by
      // "msgsndr-booking-complete" or data containing booking_completed
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        const isBookingComplete =
          data?.type === "msgsndr-booking-complete" ||
          data?.booking_completed ||
          data?.action === "booking_completed";

        if (isBookingComplete && onBookingDetected) {
          onBookingDetected();
        }
      } catch {
        // Non-JSON messages from iframe are expected; ignore
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, onBookingDetected]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoading(false);
  }, []);

  if (!calendarUrl) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: IFRAME_MIN_HEIGHT + 80,
          maxHeight: "95vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Gotham', sans-serif",
            fontWeight: 700,
            color: colors.primary,
          }}
        >
          {title || "Schedule Appointment"}
        </Typography>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          position: "relative",
          minHeight: IFRAME_MIN_HEIGHT,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {iframeLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.9)",
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: colors.primary, mb: 2 }} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Gotham', sans-serif",
                color: colors.primary,
              }}
            >
              Loading calendar...
            </Typography>
          </Box>
        )}
        <iframe
          ref={iframeRef}
          src={calendarUrl}
          title="GHL Booking Calendar"
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          style={{
            width: "100%",
            flex: 1,
            minHeight: IFRAME_MIN_HEIGHT,
            border: "none",
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default GHLCalendarModal;
